import React, { useEffect, useRef, useState } from 'react';
import { ArrowUpRight, BookOpen } from 'lucide-react';

import { DOC_LINKS } from './helpers';
import styles from './schema-editor.module.css';

const HELP_CENTER_HREF = 'https://docs.embedreach.com';

/**
 * Header "Setup guides" button that opens a popover of documentation links.
 * Replaces the doc list that previously lived in the schema sidebar.
 */
export const SetupGuidesMenu: React.FC = () => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close the popover on outside click or Escape — no render-time equivalent.
  useEffect(() => {
    if (!open) return;
    const handlePointer = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <div ref={containerRef} className={styles.guidesWrapper}>
      <button
        type="button"
        className={styles.guidesTrigger}
        data-open={open}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen(previous => !previous)}>
        <BookOpen size={15} />
        Setup guides
      </button>

      {open && (
        <div className={styles.guidesMenu}>
          <span className={styles.guidesEyebrow}>Set up your integration</span>
          {DOC_LINKS.map(link => (
            <a
              key={link.title}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.guidesLink}>
              <span className={styles.guidesLinkText}>
                <span className={styles.guidesLinkTitle}>{link.title}</span>
                <span className={styles.guidesLinkSub}>{link.description}</span>
              </span>
              <ArrowUpRight size={13} className={styles.guidesLinkIcon} />
            </a>
          ))}
          <a
            href={HELP_CENTER_HREF}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.guidesHelpCenter}>
            Visit the help center
            <ArrowUpRight size={13} />
          </a>
        </div>
      )}
    </div>
  );
};
